/**
 * MCP OAuth — node-only, CLI-gated token acquisition for hosted (OAuth-only) MCP servers.
 *
 * NOT a transport: it produces a bearer token that feeds the existing `HttpServerSpec.bearerToken`
 * path in `src/mcp.client.ts` (which stays edge-safe and untouched). The model the user asked for:
 *   - register()  = ATTENDED, one-time — PKCE + loopback callback + browser; persists {access,refresh}.
 *   - tokenFor()  = UNATTENDED — returns the cached access token; refreshes via refresh_token grant
 *                   (a plain HTTP POST, no browser) when expired; throws McpAuthError when re-auth is
 *                   genuinely required, so the CLI can prompt `--mcp-login` again.
 *
 * All side-effectful deps (fetch, browser opener, clock, store path) are injectable for headless tests.
 */
import { createServer } from 'node:http';
import { randomBytes, createHash } from 'node:crypto';
import { readFileSync, writeFileSync, mkdirSync, existsSync } from 'node:fs';
import { dirname } from 'node:path';
import { McpAuthError } from '../src/mcp.client';

export interface TokenBundle {
  access_token: string;
  refresh_token?: string;
  expires_at?: number; // epoch ms; absent = never expires
  client_id?: string;
  token_endpoint: string;
}
type Store = Record<string, TokenBundle>; // keyed by server URL

/** OAuth Authorization Server metadata (the subset we use). */
interface AsMetadata {
  authorization_endpoint: string;
  token_endpoint: string;
  registration_endpoint?: string;
}

export class McpOAuthOptions {
  /** Where to persist tokens. Mode 0600. */
  storePath = '.agent/mcp-auth.json';
  /** Injectable fetch (tests pass a fake authorization server). */
  fetchImpl: typeof fetch = fetch;
  /** Open the user's browser to the authorize URL (attended). Tests inject an auto-approver. */
  openBrowser: (url: string) => void | Promise<void> = defaultOpenBrowser;
  /** Clock (epoch ms) — injectable so token-expiry tests are deterministic. */
  now: () => number = () => Date.now();
  /** Seconds of slack before `expires_at` at which we proactively refresh. */
  skewSeconds = 60;
  /** Pre-registered client_id (used when the server doesn't support Dynamic Client Registration). */
  clientId?: string;
  /** OAuth scopes to request. */
  scope?: string;
  /** How long register() waits for the browser callback before giving up. */
  callbackTimeoutMs = 180_000;
}

export class McpOAuth {
  public options: McpOAuthOptions;
  constructor(options?: Partial<McpOAuthOptions>) {
    this.options = { ...new McpOAuthOptions(), ...options };
  }

  // -- store I/O ----------------------------------------------------------------
  private load(): Store {
    try { return existsSync(this.options.storePath) ? JSON.parse(readFileSync(this.options.storePath, 'utf8')) : {}; }
    catch { return {}; } // a corrupt store should not wedge auth — start fresh
  }
  private save(store: Store): void {
    mkdirSync(dirname(this.options.storePath), { recursive: true });
    writeFileSync(this.options.storePath, JSON.stringify(store, null, 2), { mode: 0o600 });
  }

  // -- ATTENDED: one-time authorization ----------------------------------------
  /** Run the authorization-code + PKCE flow and persist the resulting tokens for `serverUrl`. */
  async register(serverUrl: string): Promise<TokenBundle> {
    const meta = await this.discover(serverUrl);
    const clientId = this.options.clientId ?? (await this.registerClient(meta, serverUrl));
    const verifier = b64url(randomBytes(32));
    const challenge = b64url(createHash('sha256').update(verifier).digest());
    const state = b64url(randomBytes(16));

    const { code, redirectUri } = await this.captureCode(meta.authorization_endpoint, clientId, challenge, state);
    const tok = await this.exchange(meta.token_endpoint, {
      grant_type: 'authorization_code', code, redirect_uri: redirectUri, client_id: clientId, code_verifier: verifier,
    });

    const bundle: TokenBundle = {
      access_token: tok.access_token,
      refresh_token: tok.refresh_token,
      expires_at: tok.expires_in ? this.options.now() + tok.expires_in * 1000 : undefined,
      client_id: clientId,
      token_endpoint: meta.token_endpoint,
    };
    const store = this.load();
    store[serverUrl] = bundle;
    this.save(store);
    return bundle;
  }

  // -- UNATTENDED: cached token, refreshed on demand ---------------------------
  /** Return a valid access token for `serverUrl`, refreshing if expired. Throws McpAuthError if re-login is needed. */
  async tokenFor(serverUrl: string): Promise<string> {
    const store = this.load();
    const b = store[serverUrl];
    if (!b) throw new McpAuthError(401, serverUrl); // never registered → needs attended login
    const skew = this.options.skewSeconds * 1000;
    if (b.expires_at == null || b.expires_at - skew > this.options.now()) return b.access_token; // still fresh
    if (!b.refresh_token) throw new McpAuthError(401, serverUrl); // expired, nothing to refresh with

    try {
      const tok = await this.exchange(b.token_endpoint, {
        grant_type: 'refresh_token', refresh_token: b.refresh_token, client_id: b.client_id ?? '',
      });
      b.access_token = tok.access_token;
      if (tok.refresh_token) b.refresh_token = tok.refresh_token; // rotation
      b.expires_at = tok.expires_in ? this.options.now() + tok.expires_in * 1000 : undefined;
      this.save(store);
      return b.access_token;
    } catch (e) {
      throw new McpAuthError(401, serverUrl); // refresh token dead/revoked → attended re-login
    }
  }

  /** True if we hold any (even expired) credentials for `serverUrl`. */
  has(serverUrl: string): boolean { return !!this.load()[serverUrl]; }

  // -- internals ----------------------------------------------------------------
  /** Discover the Authorization Server metadata from the server's origin (RFC 8414 well-known endpoints). */
  private async discover(serverUrl: string): Promise<AsMetadata> {
    const origin = new URL(serverUrl).origin;
    const candidates = [`${origin}/.well-known/oauth-authorization-server`, `${origin}/.well-known/openid-configuration`];
    for (const u of candidates) {
      try {
        const r = await this.options.fetchImpl(u);
        if (!r.ok) continue;
        const m = await r.json();
        if (m?.authorization_endpoint && m?.token_endpoint) return m as AsMetadata;
      } catch { /* try the next candidate */ }
    }
    throw new Error(`could not discover OAuth metadata for ${serverUrl} (tried ${candidates.join(', ')})`);
  }

  /** Dynamic Client Registration (RFC 7591) when no client_id was configured and the server supports it. */
  private async registerClient(meta: AsMetadata, serverUrl: string): Promise<string> {
    if (!meta.registration_endpoint) {
      throw new Error(`${serverUrl} needs a client_id (no Dynamic Client Registration); pass clientId in options`);
    }
    const r = await this.options.fetchImpl(meta.registration_endpoint, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ client_name: 'agentx', grant_types: ['authorization_code', 'refresh_token'], token_endpoint_auth_method: 'none', redirect_uris: ['http://127.0.0.1/callback'] }),
    });
    if (!r.ok) throw new Error(`dynamic client registration failed (${r.status})`);
    const j = await r.json();
    if (!j?.client_id) throw new Error('registration response missing client_id');
    return j.client_id as string;
  }

  /** Start a one-route loopback server, open the browser, and resolve with the returned auth code. */
  private captureCode(authEndpoint: string, clientId: string, challenge: string, state: string): Promise<{ code: string; redirectUri: string }> {
    return new Promise((resolve, reject) => {
      const server = createServer((req, res) => {
        const url = new URL(req.url ?? '/', 'http://127.0.0.1');
        if (url.pathname !== '/callback') { res.writeHead(404).end(); return; }
        const code = url.searchParams.get('code');
        const gotState = url.searchParams.get('state');
        res.writeHead(200, { 'content-type': 'text/html' }).end('<h3>Authorized — you can close this tab.</h3>');
        server.close();
        clearTimeout(timer);
        if (gotState !== state) return reject(new Error('OAuth state mismatch (possible CSRF) — aborting'));
        if (!code) return reject(new Error(`authorization failed: ${url.searchParams.get('error') ?? 'no code'}`));
        resolve({ code, redirectUri });
      });
      let redirectUri = '';
      const timer = setTimeout(() => { server.close(); reject(new Error('timed out waiting for browser authorization')); }, this.options.callbackTimeoutMs);
      server.listen(0, '127.0.0.1', async () => {
        const port = (server.address() as { port: number }).port;
        redirectUri = `http://127.0.0.1:${port}/callback`;
        const auth = new URL(authEndpoint);
        auth.searchParams.set('response_type', 'code');
        auth.searchParams.set('client_id', clientId);
        auth.searchParams.set('redirect_uri', redirectUri);
        auth.searchParams.set('code_challenge', challenge);
        auth.searchParams.set('code_challenge_method', 'S256');
        auth.searchParams.set('state', state);
        if (this.options.scope) auth.searchParams.set('scope', this.options.scope);
        try { await this.options.openBrowser(auth.toString()); }
        catch (e) { server.close(); clearTimeout(timer); reject(e instanceof Error ? e : new Error(String(e))); }
      });
    });
  }

  /** POST an x-www-form-urlencoded token request and return the parsed token response. */
  private async exchange(tokenEndpoint: string, params: Record<string, string>): Promise<{ access_token: string; refresh_token?: string; expires_in?: number }> {
    const r = await this.options.fetchImpl(tokenEndpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded', accept: 'application/json' },
      body: new URLSearchParams(params).toString(),
    });
    if (!r.ok) throw new Error(`token endpoint returned ${r.status}`);
    const j = await r.json();
    if (!j?.access_token) throw new Error('token response missing access_token');
    return j;
  }
}

/** base64url with no padding (PKCE + state). */
function b64url(buf: Buffer): string {
  return buf.toString('base64').replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Best-effort cross-platform browser open (macOS `open`, Linux `xdg-open`, Windows `start`). */
function defaultOpenBrowser(url: string): void {
  const cmd = process.platform === 'darwin' ? 'open' : process.platform === 'win32' ? 'cmd' : 'xdg-open';
  const args = process.platform === 'win32' ? ['/c', 'start', '', url] : [url];
  import('node:child_process').then(({ spawn }) => spawn(cmd, args, { stdio: 'ignore', detached: true }).unref());
}
